import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import './Switch.css';

export interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
}

/**
 * Binary on/off toggle for immediate-effect settings (theme, density, feature
 * flags). For form values that submit later, prefer {@link Checkbox}. Built on a
 * hidden native checkbox with `role="switch"` for correct semantics.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, className, id, disabled, checked, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;

  return (
    // A <label> wrapper (implicit association) makes the WHOLE control — the
    // visible track included — toggle the hidden input. The previous <span>
    // wrapper left the track as a dead aria-hidden sibling: real mouse clicks on
    // the visible toggle did nothing unless a text label happened to be present.
    <label
      className={cn('switch', disabled && 'switch--disabled', className)}
      htmlFor={inputId}
    >
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        role="switch"
        className="switch__input"
        disabled={disabled}
        checked={checked}
        {...rest}
      />
      <span className="switch__track" aria-hidden="true">
        <span className="switch__thumb" />
      </span>
      {label != null && <span className="switch__label">{label}</span>}
    </label>
  );
});
